import { createButtonTokens, createSidebarTokens } from '../tokens/components.js';
import { SEMANTIC, type SemanticTokens } from '../tokens/semantic.js';

/**
 * Button Component Configuration
 * Interactive buttons with multiple severity variants
 */
export function createButton(s: SemanticTokens) {
  const buttonTokens = createButtonTokens(s);
  const sidebarTokens = createSidebarTokens(s);
  return {
    root: {
      borderRadius: buttonTokens.base.radius,
      paddingX: buttonTokens.base.paddingX,
      paddingY: buttonTokens.base.paddingY,
      gap: buttonTokens.base.gap,
      transitionDuration: buttonTokens.base.transitionDuration,
      fontWeight: buttonTokens.base.labelFontWeight,
      focusRing: {
        width: buttonTokens.base.focusRingWidth,
        style: buttonTokens.base.focusRingStyle,
        offset: buttonTokens.base.focusRingOffset,
      },
      sm: {
        fontSize: buttonTokens.sm.fontSize,
        paddingX: buttonTokens.sm.paddingX,
        paddingY: buttonTokens.sm.paddingY,
      },
      lg: {
        fontSize: buttonTokens.lg.fontSize,
        paddingX: buttonTokens.lg.padding,
        paddingY: buttonTokens.lg.padding,
      },
      label: {
        fontWeight: buttonTokens.base.labelFontWeight,
      },
      badgeSize: '.5rem',
    },
    colorScheme: {
      light: {
        root: {
          primary: {
            background: buttonTokens.primary.bg,
            hoverBackground: buttonTokens.primary.bgHover,
            activeBackground: buttonTokens.primary.bgPressed,
            borderColor: buttonTokens.primary.border,
            hoverBorderColor: buttonTokens.primary.border,
            activeBorderColor: buttonTokens.primary.border,
            color: buttonTokens.primary.fg,
            hoverColor: buttonTokens.primary.fg,
            activeColor: buttonTokens.primary.fg,
          },
          secondary: {
            background: buttonTokens.secondary.bg,
            hoverBackground: buttonTokens.secondary.bgHover,
            activeBackground: buttonTokens.secondary.bgPressed,
            borderColor: buttonTokens.secondary.border,
            hoverBorderColor: buttonTokens.secondary.border,
            activeBorderColor: buttonTokens.secondary.border,
            color: buttonTokens.secondary.fg,
            hoverColor: buttonTokens.secondary.fg,
            activeColor: buttonTokens.secondary.fg,
          },
          success: {
            background: buttonTokens.status.success.bg,
            hoverBackground: buttonTokens.status.success.bgHover,
            activeBackground: buttonTokens.status.success.bgPressed,
            borderColor: buttonTokens.status.success.border,
            hoverBorderColor: buttonTokens.status.success.border,
            activeBorderColor: buttonTokens.status.success.border,
            color: buttonTokens.status.success.fg,
            hoverColor: buttonTokens.status.success.fg,
            activeColor: buttonTokens.status.success.fg,
          },
          warn: {
            background: buttonTokens.status.warning.bg,
            hoverBackground: buttonTokens.status.warning.bgHover,
            activeBackground: buttonTokens.status.warning.bgPressed,
            borderColor: buttonTokens.status.warning.border,
            hoverBorderColor: buttonTokens.status.warning.border,
            activeBorderColor: buttonTokens.status.warning.border,
            color: buttonTokens.status.warning.fg,
            hoverColor: buttonTokens.status.warning.fg,
            activeColor: buttonTokens.status.warning.fg,
          },
          danger: {
            background: buttonTokens.status.danger.bg,
            hoverBackground: buttonTokens.status.danger.bgHover,
            activeBackground: buttonTokens.status.danger.bgPressed,
            borderColor: buttonTokens.status.danger.border,
            hoverBorderColor: buttonTokens.status.danger.border,
            activeBorderColor: buttonTokens.status.danger.border,
            color: buttonTokens.status.danger.fg,
            hoverColor: buttonTokens.status.danger.fg,
            activeColor: buttonTokens.status.danger.fg,
          },
          info: {
            background: buttonTokens.secondary.bg,
            hoverBackground: buttonTokens.secondary.bgHover,
            activeBackground: buttonTokens.secondary.bgPressed,
            borderColor: buttonTokens.secondary.border,
            hoverBorderColor: buttonTokens.secondary.border,
            activeBorderColor: buttonTokens.secondary.border,
            color: buttonTokens.secondary.fg,
            hoverColor: buttonTokens.secondary.fg,
            activeColor: buttonTokens.secondary.fg,
          },
          help: {
            background: buttonTokens.secondary.bg,
            hoverBackground: buttonTokens.secondary.bgHover,
            activeBackground: buttonTokens.secondary.bgPressed,
            borderColor: buttonTokens.secondary.border,
            hoverBorderColor: buttonTokens.secondary.border,
            activeBorderColor: buttonTokens.secondary.border,
            color: buttonTokens.secondary.fg,
            hoverColor: buttonTokens.secondary.fg,
            activeColor: buttonTokens.secondary.fg,
          },
          contrast: {
            background: buttonTokens.ghost.bg,
            hoverBackground: buttonTokens.ghost.bgHover,
            activeBackground: buttonTokens.ghost.bgPressed,
            borderColor: buttonTokens.ghost.border,
            hoverBorderColor: buttonTokens.ghost.border,
            activeBorderColor: buttonTokens.ghost.border,
            color: buttonTokens.ghost.fg,
            hoverColor: buttonTokens.ghost.fg,
            activeColor: buttonTokens.ghost.fg,
          },
        },
        outlined: {
          primary: {
            borderColor: buttonTokens.outlined.border,
            color: buttonTokens.outlined.fg,
          },
        },
        text: {
          primary: {
            color: buttonTokens.text.fg,
          },
        },
        link: {
          color: buttonTokens.link.fg,
          hoverColor: buttonTokens.link.fgHover,
        },
      },
    },
    css: () => `
    :root .p-button,
    .p-button.p-button-primary,
    .p-button.p-button-secondary,
    .p-button.p-button-contrast,
    .p-button.p-button-success,
    .p-button.p-button-warn,
    .p-button.p-button-danger {
      font-weight: ${buttonTokens.base.labelFontWeight};
    }

    /* Focus ring - primary color for all buttons */
    :root .p-button:focus-visible {
      outline-color: ${buttonTokens.base.focusRingColor};
      box-shadow: none;
    }

    /* A button never sacrifices its own box: it keeps its padding instead of
       shrinking inside a tight flex row, and its label never wraps or clips. */
    :root .p-button {
      flex-shrink: 0;
      white-space: nowrap;
    }

    /* Base button styles */
    :root .p-button .p-button-label {
      line-height: ${buttonTokens.base.labelLineHeight};
      font-weight: ${buttonTokens.base.labelFontWeight};
      white-space: nowrap;
    }

    /* Base disabled state - use colors instead of opacity */
    .p-button:disabled,
    .p-button.p-disabled {
      opacity: 1;
      cursor: not-allowed;
      background: ${buttonTokens.primary.bgDisabled};
      border-color: ${buttonTokens.primary.bgDisabled};
      color: ${buttonTokens.primary.fgDisabled};
    }

    .p-button.p-button-secondary:disabled,
    .p-button.p-button-contrast:disabled,
    .p-button.p-button-success:disabled,
    .p-button.p-button-warn:disabled,
    .p-button.p-button-danger:disabled {
      background: ${buttonTokens.primary.bgDisabled};
      border-color: ${buttonTokens.primary.bgDisabled};
      color: ${buttonTokens.primary.fgDisabled};
    }

    /* Icon-only button styles */
    :root .p-button.p-button-icon-only {
      gap: 0;
      line-height: 0;
      padding-inline-start: 0;
      padding-inline-end: 0;
    }

    /* Hover state for anchor tag buttons - regular severities */
    a.p-button:not(:disabled):hover,
    a.p-button:not(:disabled):active {
      text-decoration: none;
    }

    a.p-button.p-button-primary:not(:disabled):hover {
      background: ${buttonTokens.primary.bgHover};
    }

    a.p-button.p-button-secondary:not(:disabled):hover {
      background: ${buttonTokens.secondary.bgHover};
    }

    a.p-button.p-button-success:not(:disabled):hover {
      background: ${buttonTokens.status.success.bgHover};
    }

    a.p-button.p-button-warn:not(:disabled):hover {
      background: ${buttonTokens.status.warning.bgHover};
    }

    a.p-button.p-button-danger:not(:disabled):hover {
      background: ${buttonTokens.status.danger.bgHover};
    }

    a.p-button.p-button-info:not(:disabled):hover {
      background: ${buttonTokens.secondary.bgHover};
    }

    a.p-button.p-button-help:not(:disabled):hover {
      background: ${buttonTokens.secondary.bgHover};
    }

    a.p-button.p-button-contrast:not(:disabled):hover {
      background: ${buttonTokens.ghost.bgHover};
    }

    /* Link button variant - primary */
    :root .p-button.p-button-link,
    :root a.p-button.p-button-link {
      min-height: auto;
      font-size: ${buttonTokens.link.fontSize};
      line-height: ${buttonTokens.link.lineHeight};
      font-weight: ${buttonTokens.link.fontWeight};
      padding: 0;
      border-radius: 0;
      gap: ${buttonTokens.link.gap};
      transition: color ${buttonTokens.link.transition};
      text-underline-offset: 4px;
      background: transparent;
    }

    :root .p-button.p-button-link:hover,
    :root a.p-button.p-button-link:hover {
      background: transparent;
    }

    :root .p-button.p-button-link .p-button-label,
    :root a.p-button.p-button-link .p-button-label {
      font-size: ${buttonTokens.link.fontSize};
      font-weight: ${buttonTokens.link.fontWeight};
      line-height: ${buttonTokens.link.lineHeight};
    }

    :root .p-button.p-button-link.p-button-label:hover,
    :root a.p-button.p-button-link.p-button-label:hover {
      text-decoration: underline;
      text-underline-offset: 4px;
    }

    :root .p-button.p-button-link .p-button-icon,
    :root a.p-button.p-button-link .p-button-icon {
      font-size: ${buttonTokens.link.iconSize};
    }

    /* Link button - secondary severity */
    :root .p-button.p-button-link.p-button-secondary,
    :root a.p-button.p-button-link.p-button-secondary {
      color: ${buttonTokens.link.secondaryFg};
    }

    :root .p-button.p-button-link.p-button-secondary.p-button-label:hover,
    :root a.p-button.p-button-link.p-button-secondary.p-button-label:hover {
      text-decoration: underline;
      text-underline-offset: 4px;
      color: ${buttonTokens.link.secondaryFg};
    }

    .p-button.p-button-link.p-button-secondary .p-button-icon:hover,
    a.p-button.p-button-link.p-button-secondary .p-button-icon:hover {
      color: ${buttonTokens.link.secondaryFg};
    }

    /* Link button - semibold variant (pButtonLinkSemibold attribute) */
    p-button[pButtonLinkSemibold] .p-button.p-button-link,
    p-button[pButtonLinkSemibold] a.p-button.p-button-link,
    .p-button.p-button-link[pButtonLinkSemibold] {
      font-weight: ${buttonTokens.link.fontWeightSem};
    }

    p-button[pButtonLinkSemibold] .p-button.p-button-link .p-button-label,
    p-button[pButtonLinkSemibold] a.p-button.p-button-link .p-button-label,
    .p-button.p-button-link[pButtonLinkSemibold] .p-button-label {
      font-weight: ${buttonTokens.link.fontWeightSem};
    }

    /* Text button variant */
    .p-button.p-button-text:hover,
    a.p-button.p-button-text:hover {
      text-decoration: underline;
      text-underline-offset: 4px;
    }

    /* Utility button variant - directive attribute selector */
    p-button[pButtonUtility] .p-button,
    .p-button[pButtonUtility] {
      width: ${buttonTokens.utility.size};
      height: ${buttonTokens.utility.size};
      border-radius: ${buttonTokens.base.radius};
      border: none;
      background: ${buttonTokens.utility.bg};
      color: ${buttonTokens.utility.fg};
      transition: background-color ${buttonTokens.utility.transition}, color ${buttonTokens.utility.transition};
    }

    p-button[pButtonUtility] .p-button-icon,
    .p-button[pButtonUtility] .p-button-icon {
      font-size: ${buttonTokens.utility.iconSize};
      margin: 0;
    }

    p-button[pButtonUtility] .p-button-label,
    .p-button[pButtonUtility] .p-button-label {
      display: none;
    }

    p-button[pButtonUtility] .p-button:not(:disabled):hover,
    .p-button[pButtonUtility]:not(:disabled):hover,
    a.p-button[pButtonUtility]:not(:disabled):hover {
      background: ${buttonTokens.utility.bgHover};
      color: ${buttonTokens.utility.fg};
      border-color: transparent;
    }

    p-button[pButtonUtility] .p-button:not(:disabled):active,
    .p-button[pButtonUtility]:not(:disabled):active {
      background: ${buttonTokens.utility.bgPressed};
      color: ${buttonTokens.utility.fg};
      border-color: transparent;
    }

    p-button[pButtonUtility] .p-button:disabled,
    p-button[pButtonUtility] .p-button.p-disabled,
    .p-button[pButtonUtility]:disabled,
    .p-button[pButtonUtility].p-disabled {
      background: ${buttonTokens.utility.bgDisabled};
      color: ${buttonTokens.utility.fgDisabled};
      border-color: ${buttonTokens.utility.bgDisabled};
    }

    /* Toolbar secondary override - used by nbx-menu-button */
    p-button[pButtonUtility] .p-button.p-toolbar-secondary,
    p-button[pButtonUtility].p-toolbar-secondary .p-button,
    .p-button[pButtonUtility].p-toolbar-secondary {
      border-radius: ${buttonTokens.base.radius};
      background: ${buttonTokens.action.bg};
      color: ${buttonTokens.action.fg};
      border: none;
    }

    p-button[pButtonUtility] .p-button.p-toolbar-secondary:not(:disabled):hover,
    p-button[pButtonUtility].p-toolbar-secondary .p-button:not(:disabled):hover,
    .p-button[pButtonUtility].p-toolbar-secondary:not(:disabled):hover {
      background: ${buttonTokens.action.bgHover};
      color: ${buttonTokens.action.fgHover};
      border: none;
    }

    p-button[pButtonUtility] .p-button.p-toolbar-secondary:not(:disabled):hover .p-button-icon,
    p-button[pButtonUtility].p-toolbar-secondary .p-button:not(:disabled):hover .p-button-icon,
    .p-button[pButtonUtility].p-toolbar-secondary:not(:disabled):hover .p-button-icon {
      color: ${buttonTokens.action.fgHover};
    }

    p-button[pButtonUtility] .p-button.p-toolbar-secondary:disabled,
    p-button[pButtonUtility] .p-button.p-toolbar-secondary.p-disabled,
    p-button[pButtonUtility].p-toolbar-secondary .p-button:disabled,
    p-button[pButtonUtility].p-toolbar-secondary .p-button.p-disabled,
    .p-button[pButtonUtility].p-toolbar-secondary:disabled,
    .p-button[pButtonUtility].p-toolbar-secondary.p-disabled {
      background: ${buttonTokens.action.bgDisabled};
      color: ${buttonTokens.action.fgDisabled};
      border: none;
    }

    /* Action button variant - directive attribute selector */
    p-button[pButtonAction] .p-button,
    .p-button[pButtonAction] {
      min-width: ${buttonTokens.action.size};
      height: ${buttonTokens.action.size};
      padding-block: 0;
      border: none;
      background: ${buttonTokens.action.bg};
      color: ${buttonTokens.action.fg};
      font-weight: ${buttonTokens.action.fontWeight};
      font-size: ${buttonTokens.action.fontSize};
      transition: background-color ${buttonTokens.action.transition}, color ${buttonTokens.action.transition};
    }

    p-button[pButtonAction] .p-button.p-button-icon-only,
    .p-button[pButtonAction].p-button-icon-only {
      width: ${buttonTokens.action.size};
    }

    p-button[pButtonAction] .p-button-icon,
    .p-button[pButtonAction] .p-button-icon {
      font-size: ${buttonTokens.action.iconSize};
      margin: 0;
      color: ${buttonTokens.action.fg};
    }

    p-button[pButtonAction] .p-button-label,
    .p-button[pButtonAction] .p-button-label {
      font-weight: ${buttonTokens.action.fontWeight};
      font-size: ${buttonTokens.action.fontSize};
    }

    p-button[pButtonAction] .p-button:not(:disabled):hover,
    .p-button[pButtonAction]:not(:disabled):hover {
      background: ${buttonTokens.action.bgHover};
      color: ${buttonTokens.action.fgHover};
      border: none;
    }

    p-button[pButtonAction] .p-button:not(:disabled):hover .p-button-icon,
    .p-button[pButtonAction]:not(:disabled):hover .p-button-icon {
      color: ${buttonTokens.action.fgHover};
    }

    p-button[pButtonAction] .p-button:not(:disabled):active,
    .p-button[pButtonAction]:not(:disabled):active {
      background: ${buttonTokens.action.bgPressed};
      color: ${buttonTokens.action.fgHover};
      border: none;
    }

    p-button[pButtonAction] .p-button:not(:disabled):active .p-button-icon,
    .p-button[pButtonAction]:not(:disabled):active .p-button-icon {
      color: ${buttonTokens.action.fgHover};
    }

    p-button[pButtonAction] .p-button:disabled,
    p-button[pButtonAction] .p-button.p-disabled,
    .p-button[pButtonAction]:disabled,
    .p-button[pButtonAction].p-disabled {
      background: ${buttonTokens.action.bgDisabled};
      color: ${buttonTokens.action.fgDisabled};
      border: none;
    }

    p-button[pButtonAction] .p-button:disabled .p-button-icon,
    p-button[pButtonAction] .p-button.p-disabled .p-button-icon,
    .p-button[pButtonAction]:disabled .p-button-icon,
    .p-button[pButtonAction].p-disabled .p-button-icon {
      color: ${buttonTokens.action.fgDisabled};
    }

    /* Inline button variant - directive attribute selector */
    .p-button[pButtonInline] {
      width: ${buttonTokens.inline.size};
      height: ${buttonTokens.inline.size};
      padding: 0;
      border: none;
      border-radius: ${buttonTokens.base.radius};
      background: transparent;
      color: ${buttonTokens.inline.fg};
      transition: color ${buttonTokens.inline.transition};
    }

    .p-button[pButtonInline] .p-button-icon {
      font-size: ${buttonTokens.inline.iconSize};
      margin: 0;
    }

    .p-button[pButtonInline] .p-button-label {
      display: none;
    }

    .p-button[pButtonInline]:not(:disabled):hover,
    .p-button[pButtonInline]:not(:disabled):active {
      background: transparent;
      color: ${buttonTokens.inline.fgHover};
      border: none;
    }

    nbx-dialog-outlet .p-button[pButtonInline]:has(nbx-close-icon):not(:disabled):hover,
    nbx-dialog-outlet .p-button[pButtonInline]:has(nbx-close-icon):not(:disabled):active {
      color: ${buttonTokens.inline.fg};
    }

    .p-button[pButtonInline]:disabled {
      background: transparent;
      color: ${buttonTokens.inline.fgDisabled};
    }

    /* Sidebar button variant - directive attribute selector */
    p-button[pButtonSidebar] .p-button,
    button.p-button[pButtonSidebar],
    a.p-button[pButtonSidebar] {
      width: 100%;
      gap: ${buttonTokens.sidebar.gap};
      padding: ${buttonTokens.sidebar.padding};
      border-radius: 0;
      border-right: none;
      border-bottom: none;
      border-top: none;
      border-left: ${buttonTokens.sidebar.borderWidth} solid transparent;
      background: ${sidebarTokens.bgDefault};
      color: ${sidebarTokens.fgDefault};
      font-weight: ${buttonTokens.sidebar.fontWeightNormal};
      justify-content: flex-start;
      transition: background-color ${buttonTokens.sidebar.transition}, border-color ${buttonTokens.sidebar.transition};
    }

    p-button[pButtonSidebar] .p-button-icon,
    button.p-button[pButtonSidebar] .p-button-icon,
    a.p-button[pButtonSidebar] .p-button-icon {
      color: ${sidebarTokens.fgDefault};
      font-size: ${buttonTokens.sidebar.iconSize};
      margin: 0;
      flex-shrink: 0;
      display: inline-block;
    }

    p-button[pButtonSidebar] .p-button-label,
    button.p-button[pButtonSidebar] .p-button-label,
    a.p-button[pButtonSidebar] .p-button-label {
      flex: 1;
      text-align: left;
      line-height: normal;
      font-weight: ${buttonTokens.sidebar.fontWeightNormal};
    }

    /* Hover state - not selected */
    p-button[pButtonSidebar]:not(.selected) .p-button:hover,
    button.p-button[pButtonSidebar]:not(.selected):hover,
    a.p-button[pButtonSidebar]:not(.selected):hover {
      background: ${sidebarTokens.bgHover};
      border-right: none;
      border-top: none;
      border-bottom: none;
      border-left: ${buttonTokens.sidebar.borderWidth} solid ${buttonTokens.sidebar.border};
      color: ${sidebarTokens.fgHover};
      text-decoration: none;
    }

    /* Selected state */
    p-button.selected[pButtonSidebar] .p-button,
    p-button.selected[pButtonSidebar] .p-button:hover,
    button.p-button[pButtonSidebar].selected,
    button.p-button[pButtonSidebar].selected:hover,
    a.p-button[pButtonSidebar].selected,
    a.p-button[pButtonSidebar].selected:hover {
      background: ${sidebarTokens.bgActive};
      font-weight: ${buttonTokens.sidebar.fontWeightBold};
      border-right: none;
      border-top: none;
      border-bottom: none;
      border-left: ${buttonTokens.sidebar.borderWidth} solid ${buttonTokens.sidebar.border};
      color: ${sidebarTokens.fgActive};
    }

    p-button.selected[pButtonSidebar] .p-button-label,
    button.p-button[pButtonSidebar].selected .p-button-label,
    a.p-button[pButtonSidebar].selected .p-button-label {
      font-weight: ${buttonTokens.sidebar.fontWeightBold};
      color: ${sidebarTokens.fgActive};
    }

    p-button.selected[pButtonSidebar] .p-button-icon,
    button.p-button[pButtonSidebar].selected .p-button-icon,
    a.p-button[pButtonSidebar].selected .p-button-icon {
      color: ${sidebarTokens.fgActive};
    }
  `,
  };
}
export const button = createButton(SEMANTIC);
